import { IntegrationEnhanced } from "@/api/integrations";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useConnectionsCore } from "./useConnectionsCore";

// Helper function to get category display name
function getCategoryDisplayName(category: string): string {
  return category
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Get all unique categories from integrations
function getAllCategories(integrations: IntegrationEnhanced[]): string[] {
  const categories = new Set<string>();
  integrations.forEach((integration) => {
    integration.metadata?.categories?.forEach((category) => {
      categories.add(category);
    });
  });
  return Array.from(categories).sort();
}

// Sort function for connected integrations only (alphabetically)
const sortConnectedIntegrations = (
  a: IntegrationEnhanced,
  b: IntegrationEnhanced,
) => {
  return a.name.localeCompare(b.name);
};

interface UseConnectionsSearchProps {
  context?: "page" | "pane";
}

export function useConnectionsSearch({
  context = "page",
}: UseConnectionsSearchProps = {}) {
  // Use the core hook
  const coreHook = useConnectionsCore({ context });
  const {
    integrations,
    discoverableIntegrations,
    allIntegrations,
    connectedIntegrationIds,
  } = coreHook;

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showConnected, setShowConnected] = useState(true);

  // Get all categories
  const allCategories = useMemo(() => {
    return getAllCategories(allIntegrations);
  }, [allIntegrations]);

  // Create Fuse instance for fuzzy search
  const integrationSearch = useMemo(() => {
    if (!allIntegrations.length) return null;

    return new Fuse(allIntegrations, {
      keys: ["name"],
      threshold: 0.2,
    });
  }, [allIntegrations]);

  // Helper function to apply filters to integration list (preserves API order)
  const applyFilters = (integrationList: IntegrationEnhanced[]) => {
    if (!integrationList.length) return [];

    let filtered = integrationList;

    // Apply search filter
    if (searchTerm.trim() !== "" && integrationSearch) {
      const result = integrationSearch.search(searchTerm);
      const searchResults = result?.map((result) => result.item) || [];
      filtered = integrationList.filter((integration) =>
        searchResults.some((searchItem) => searchItem.id === integration.id),
      );
    }

    // Apply category filter
    filtered = filtered.filter((integration) => {
      if (selectedCategory === "all" || !selectedCategory) return true;
      return integration.metadata?.categories?.includes(selectedCategory);
    });

    return filtered;
  };

  // Filter enabled integrations (org integrations) - preserves API order
  const filteredEnabledIntegrations = useMemo(() => {
    return applyFilters(integrations);
  }, [integrations, searchTerm, selectedCategory, integrationSearch]);

  // Filter discoverable integrations (exclude already connected ones) - preserves API order
  const filteredDiscoverableIntegrations = useMemo(() => {
    const filtered = applyFilters(discoverableIntegrations);
    return filtered.filter(
      (integration) => !connectedIntegrationIds.has(integration.id),
    );
  }, [
    discoverableIntegrations,
    searchTerm,
    selectedCategory,
    integrationSearch,
    connectedIntegrationIds,
  ]);

  // Separate connected and available integrations from enabled only
  // Connected: sorted alphabetically, Available: preserves API order (starred first, then by weight)
  const connectedIntegrations = useMemo(() => {
    return filteredEnabledIntegrations
      .filter((integration) => connectedIntegrationIds.has(integration.id))
      .sort(sortConnectedIntegrations);
  }, [filteredEnabledIntegrations, connectedIntegrationIds]);

  const availableIntegrations = useMemo(() => {
    return filteredEnabledIntegrations.filter(
      (integration) => !connectedIntegrationIds.has(integration.id),
    );
  }, [filteredEnabledIntegrations, connectedIntegrationIds]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setShowConnected(true);
  };

  // Zero state logic
  const showZeroState =
    connectedIntegrations.length === 0 &&
    availableIntegrations.length === 0 &&
    filteredDiscoverableIntegrations.length === 0;

  return {
    // Include everything from the core hook
    ...coreHook,

    // Override with filtered data
    discoverableIntegrations: filteredDiscoverableIntegrations,
    connectedIntegrations,
    availableIntegrations,

    // Search & filter states
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    showConnected,
    setShowConnected,

    // Search-specific data
    allCategories,

    // Search utilities
    handleClearFilters,
    showZeroState,
    getCategoryDisplayName,
  };
}
